import { useEffect, useMemo, useState } from "react";
import { Gantt, Task, ViewMode } from "gantt-task-react";
import dayjs from "dayjs";

import "gantt-task-react/dist/index.css";
import "@/styles/gantt.css";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";

import { transformTask } from "@/routes/(planner-screens)/utils/utils";

function AiGanttChart({ data }: { data: any }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [view, setView] = useState<ViewMode>(ViewMode.Day);
  const [isChecked, setIsChecked] = useState<boolean>(true);
  const [isRendered, setIsRendered] = useState<boolean>(false);

  let columnWidth = 50;
  let containerWidth = "";
  if (view === ViewMode.HalfDay) {
    columnWidth = 100;
    containerWidth = "w-1/4";
  } else if (view === ViewMode.Day) {
    containerWidth = "w-1/2";
  } else if (view === ViewMode.Month) {
    columnWidth = 150;
    containerWidth = "w-full";
  } else if (view === ViewMode.Week) {
    columnWidth = 250;
    containerWidth = "w-3/4";
  }

  useMemo(() => {
    if (data) {
      const filtersDate = data.filter(
        (item: any) => item?.startDate && item?.dueDate
      );

      const transformedTask =
        filtersDate.length > 0
          ? filtersDate
              .map((job: any) => transformTask(job))
              .filter((task: any): task is Task => task !== null)
          : [];

      setTasks(transformedTask);
      setIsRendered(true);
    }
  }, [data]);

  useEffect(() => {
    if (isRendered) {
      const todayElement = document.querySelector(".today");
      if (todayElement) {
        todayElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  }, [isRendered]);

  return (
    <div className={`${containerWidth} aero:h-[80vh]`}>
      <Card>
        <CardContent>
          {/* Toolbar */}
          <div className="aero:flex aero:items-center aero:justify-between aero:space-x-4 aero:mb-2 aero:p-4 aero:!rounded-lg aero:bg-card">
            <div className="aero:flex aero:items-center aero:space-x-2">
              <Select onValueChange={(value) => setView(value as ViewMode)}>
                <SelectTrigger className="aero:w-[180px]">
                  <SelectValue placeholder="Select view" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ViewMode.QuarterDay}>
                    Quarter Of Day
                  </SelectItem>
                  <SelectItem value={ViewMode.HalfDay}>Half Day</SelectItem>
                  <SelectItem value={ViewMode.Day}>Day</SelectItem>
                  <SelectItem value={ViewMode.Week}>Week</SelectItem>
                  <SelectItem value={ViewMode.Month}>Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="aero:flex aero:items-center aero:space-x-2">
              <Switch
                id="show-task-list"
                checked={isChecked}
                onCheckedChange={setIsChecked}
              />
              <Label htmlFor="show-task-list">Show Task List</Label>
            </div>
          </div>

          {/*Gantt Chart */}
          {tasks.length === 0 ? (
            <Card className="aero:w-full aero:flex aero:justify-center aero:items-center aero:h-60">
              <CardContent>Currently no data to display</CardContent>
            </Card>
          ) : (
            <Gantt
              tasks={tasks}
              viewMode={view}
              TooltipContent={Tooltip}
              listCellWidth={isChecked ? "155px" : ""}
              barCornerRadius={4}
              todayColor="#d8e4fa"
              columnWidth={columnWidth}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * This Tooltip is used for Gantt Chart custom tooltip only
 * @param task
 * @returns
 */
const Tooltip = ({ task }: { task: Task }) => (
  <Card className="aero:shadow-lg aero:border-none">
    <CardContent className="aero:p-3 aero:space-y-1">
      <p className="aero:!font-medium aero:!text-sm">{task.name}</p>
      <div className="aero:!text-xs aero:space-y-0.5 aero:!text-muted-foreground">
        <p>Start: {dayjs(task.start).format("MM/DD/YYYY")}</p>
        <p>End: {dayjs(task.end).format("MM/DD/YYYY")}</p>
        <p>Progress: {task.progress || 0}%</p>
      </div>
    </CardContent>
  </Card>
);

export default AiGanttChart;
